import { useEffect, useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Target, Eye, Heart, Award, Sparkles } from 'lucide-react';
import { api, mediaUrl } from '@/admin/api';

const AboutPage = () => {
  const [content, setContent] = useState({ mission: '', vision: '', history: '' });

  useEffect(() => {
    api.get('/api/content/about').then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const convener = {
    name: 'HRM Prof. Mrs. Felicia Hembadoon Ayatse',
    position: 'Convener',
    image: '/api/media/file/mzough-tiv/media/c9a9dfe6948d4ce4a8f381c1fd8b6477.webp',
  };

  const values = [
    {
      icon: Heart,
      title: 'Unity',
      description: 'We believe in the power of togetherness and collective action for the advancement of all Tiv women.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in all our endeavors, setting high standards for leadership and service.',
    },
    {
      icon: Sparkles,
      title: 'Cultural Pride',
      description: 'We celebrate and preserve our rich Tiv heritage, traditions, and customs for future generations.',
    },
    {
      icon: Users,
      title: 'Empowerment',
      description: 'We empower women through education, skill development, and creating opportunities for growth.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              About Us
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Learn about our history, mission, and the women leading our organization forward.
            </p>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={mediaUrl('/api/media/file/mzough-tiv/media/4159922aeb8945f58553324f590e1f8d.webp')}
                alt="About Us — Sisterhood"
                className="rounded-2xl shadow-elevated object-cover w-full h-[500px]"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground">
                Our History
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed whitespace-pre-wrap" data-testid="about-history">
                {content.history || `Mzough U Kasev Tiv was founded with a vision to unite Tiv women across Nigeria under one umbrella organization. Our founding mothers recognized the need for a structured platform where Tiv women could come together to address common challenges, celebrate their heritage, and create opportunities for collective growth.`}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 hover:border-primary transition-elegant shadow-medium">
              <CardContent className="pt-8 pb-8 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Target className="text-primary" size={32} />
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-foreground">Our Mission</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-wrap" data-testid="about-mission">
                  {content.mission || 'To unite, empower, and support Tiv women through advocacy, education, cultural preservation, and economic development initiatives that promote leadership, dignity, and sustainable progress for all members and their communities.'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-accent transition-elegant shadow-medium">
              <CardContent className="pt-8 pb-8 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center">
                    <Eye className="text-accent" size={32} />
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-foreground">Our Vision</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-wrap" data-testid="about-vision">
                  {content.vision || 'To become the leading platform for Tiv women\'s empowerment, recognized nationally and internationally for excellence in cultural preservation, leadership development, and creating lasting positive change in our communities.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-medium transition-elegant">
                  <CardContent className="pt-8 pb-6 space-y-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary">
                      <Icon size={32} />
                    </div>
                    <h3 className="text-xl font-serif font-semibold text-foreground">{value.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Convener */}
      <section className="py-20 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-4">
              Our Convener
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Leading the vision, uniting the daughters of Tiv
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <Card className="overflow-hidden hover:shadow-elevated transition-elegant group" data-testid="convener-card">
              <div className="aspect-[3/4] overflow-hidden bg-muted">
                <img
                  src={mediaUrl(convener.image)}
                  alt={convener.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardContent className="pt-8 pb-8 text-center space-y-2">
                <p className="text-sm text-primary font-medium tracking-wide uppercase">{convener.position}</p>
                <h3 className="text-2xl font-serif font-semibold text-foreground">{convener.name}</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Cultural Significance */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground">
                Cultural Significance of Tiv Women
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  In Tiv culture, women hold a special place as the backbone of families and communities. They are
                  the keepers of traditions, the nurturers of future generations, and the bridge between past and
                  present.
                </p>
                <p>
                  Tiv women are known for their resilience, creativity, and strong sense of community. From
                  agriculture to arts and crafts, from trade to traditional medicine, women have always been at the
                  forefront of sustaining and enriching Tiv cultural heritage.
                </p>
                <p>
                  Our organization seeks to honor this legacy while empowering modern Tiv women to excel in all
                  spheres of life, ensuring that our rich cultural identity continues to thrive in contemporary
                  society.
                </p>
              </div>
            </div>
            <div>
              <img
                src={mediaUrl('/api/media/file/mzough-tiv/media/8a1105f9e4d242d18129e4a1415b5d50.webp')}
                alt="Cultural Celebration — Tiv Women Summit 2025"
                className="rounded-2xl shadow-elevated object-cover w-full h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
